import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Tiny in-memory + sessionStorage cache so the banner decision is stable
// across navigations within a single visit (no flicker between routes).
const CACHE_KEY = "tco_welcome_offer_v1";
let inFlight = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeCache(key, value) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ key, value })); } catch (_) {}
}

/**
 * "₹50 OFF your first order over ₹199" banner.
 *
 * Anti-flicker strategy:
 *   1. Wait for auth `loading` to complete before firing the eligibility
 *      request. If we fetched before auth resolves, the guest response would
 *      briefly render, then swap to the signed-in response — the source of
 *      the reported "flashing".
 *   2. Cache the eligibility result in sessionStorage keyed by the resolved
 *      email so subsequent route changes render the correct state on the
 *      first paint.
 *   3. Return `null` until the check is fully resolved. Never show one state
 *      then swap.
 */
export default function WelcomeOfferBanner({ variant = "default" }) {
  const { user, loading: authLoading } = useAuth();
  const cacheKey = authLoading ? null : (user?.email?.toLowerCase() || "guest");
  const [offer, setOffer] = useState(() => {
    // Hydrate from cache on first render for a jump-free paint.
    const cached = readCache();
    return cached && cached.key === cacheKey ? cached.value : null;
  });
  const [resolved, setResolved] = useState(() => {
    const cached = readCache();
    return !!(cached && cached.key === cacheKey);
  });

  useEffect(() => {
    if (authLoading) return;                 // wait for auth to settle
    const key = user?.email?.toLowerCase() || "guest";
    const cached = readCache();
    if (cached && cached.key === key) {
      setOffer(cached.value);
      setResolved(true);
      return;
    }
    // Dedupe concurrent requests when several banners mount at once.
    if (!inFlight) {
      inFlight = api
        .get("/welcome-offer/status", { params: user?.email ? { email: user.email } : {} })
        .then(({ data }) => data)
        .finally(() => { inFlight = null; });
    }
    inFlight
      .then((data) => {
        writeCache(key, data);
        setOffer(data);
        setResolved(true);
      })
      .catch(() => {
        setOffer(null);
        setResolved(true);
      });
  }, [authLoading, user?.email]);

  // Silent while we're still figuring things out — no premature render.
  if (!resolved) return null;
  if (!offer?.eligible) return null;

  if (variant === "compact") {
    return (
      <div data-testid="welcome-offer-banner-compact" className="border border-gold/50 bg-gold/5 px-4 py-3 rounded-sm">
        <p className="font-serif italic text-espresso text-sm">
          <span className="text-gold-hover">Welcome offer · </span>
          ₹{offer.discount.toFixed(0)} OFF your first order over ₹{offer.min_subtotal.toFixed(0)}.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="welcome-offer-banner" className="bg-espresso px-4 py-2 text-center font-serif text-xs italic tracking-wide text-ivory sm:py-2.5 sm:text-sm">
      <span className="hidden text-gold sm:inline">Welcome offer · </span>
      ₹{offer.discount.toFixed(0)} OFF your first order over ₹{offer.min_subtotal.toFixed(0)}.
      {!user && (
        <Link to="/login" className="ml-2 text-[10px] not-italic font-bold uppercase tracking-[0.16em] text-ivory underline underline-offset-4 sm:ml-3 sm:label-xs">
          Sign in →
        </Link>
      )}
    </div>
  );
}
